import React, { useEffect, useState, useCallback } from 'react';
import { Mail, Phone, Calendar, Trash2, Tag, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../Context/AuthContext';
import Swal from 'sweetalert2';
import { API_URL } from '../../config';

const AdminQuotes = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();
  const navigate = useNavigate();

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchQuotes = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/admin/quotes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setQuotes(data);
      }
    } catch (err) {
      console.error('Teklifler yüklenemedi:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchQuotes();
  }, [fetchQuotes]);

  const handleDelete = async (id, source) => {
    const result = await Swal.fire({
      title: 'Emin misiniz?',
      text: "Bu teklif talebini silmek istiyor musunuz?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#334155',
      confirmButtonText: 'Evet, Sil!',
      background: '#0f172a',
      color: '#fff'
    });

    if (result.isConfirmed) {
      try {
        const url = source ? `${API_URL}/admin/quotes/${id}?source=${source}` : `${API_URL}/admin/quotes/${id}`;
        const response = await fetch(url, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          Swal.fire({ icon: 'success', title: 'Silindi!', background: '#0f172a', color: '#fff', timer: 1500 });
          fetchQuotes();
        }
      } catch (err) {
        Swal.fire({ icon: 'error', title: 'Hata!', background: '#0f172a', color: '#fff' });
      }
    }
  };

  // Pagination Logic
  const totalPages = Math.ceil(quotes.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentQuotes = quotes.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading && quotes.length === 0) return <div className="admin-card" style={{ padding: '2rem', textAlign: 'center' }}>Yükleniyor...</div>;

  return (
    <div className="admin-quotes">
      <div style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 className="admin-card-title" style={{ fontSize: 'clamp(1.5rem, 4vw, 2.2rem)', marginBottom: '0.25rem' }}>Teklif Yönetimi</h1>
          <p style={{ color: 'var(--text-dim)', fontSize: 'clamp(0.85rem, 2vw, 1rem)' }}>Gelen özel teklif taleplerini ve araç satım başvurularını buradan takip edip yönetebilirsiniz. ({quotes.length} Teklif)</p>
        </div>
      </div>

      <div className="admin-card" style={{ borderRadius: '1.25rem', border: '1px solid var(--navbar-border)', padding: 0, overflow: 'hidden', marginBottom: '2rem' }}>
        <div className="admin-table-wrapper">
          <table className="admin-table" style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
            <thead style={{ background: 'rgba(255,255,255,0.02)' }}>
              <tr>
                <th style={{ padding: '1.5rem' }}>Müşteri</th>
                <th style={{ padding: '1.5rem' }}>Araç / Hizmet</th>
                <th style={{ padding: '1.5rem' }}>Detaylar</th>
                <th style={{ padding: '1.5rem' }}>Durum</th>
                <th style={{ padding: '1.5rem' }}>Tarih</th>
                <th style={{ padding: '1.5rem', textAlign: 'right' }}>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {currentQuotes.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-dim)' }}>Henüz teklif talebi bulunmuyor.</td>
                </tr>
              ) : (
                currentQuotes.map((item, idx) => (
                  <tr key={`${item.id}-${item.source || idx}`} style={{ borderBottom: '1px solid var(--navbar-border)' }}>
                    <td style={{ padding: '1.5rem' }}>
                      <div style={{ fontWeight: '700' }}>{item.name} {item.surname}</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.75rem', color: 'var(--text-dim)', marginTop: '0.25rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                          <Phone size={12} color="var(--primary)" /> {item.phone}
                        </div>
                        {item.email && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                            <Mail size={12} color="var(--primary)" /> {item.email}
                          </div>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '1.5rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        {item.vehicle_image ? (
                          <img 
                            src={item.vehicle_image} 
                            alt={item.vehicle_name} 
                            style={{ width: '60px', height: '40px', borderRadius: '6px', objectFit: 'cover', border: '1px solid var(--navbar-border)' }} 
                          />
                        ) : (
                          <div style={{ width: '60px', height: '40px', borderRadius: '6px', background: 'var(--bg-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Tag size={18} color="var(--text-dim)" />
                          </div>
                        )}
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: '600', color: 'var(--primary)' }}>
                            <Tag size={14} /> {item.vehicle_name || 'Araç Seçilmedi'}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)', marginTop: '0.25rem' }}>
                            {item.service_type === 'rental' ? 'Araç Kiralama' : item.service_type === 'Araç Satımı' ? 'Araç Satımı' : 'Araç Satın Alma'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '1.5rem', minWidth: '280px' }}>
                      <div style={{ 
                        background: 'rgba(255, 255, 255, 0.03)', 
                        padding: '1rem', 
                        borderRadius: '1rem', 
                        border: '1px solid rgba(255, 255, 255, 0.05)',
                        position: 'relative'
                      }}>
                        <div style={{ 
                          fontSize: '0.7rem', 
                          fontWeight: '800', 
                          color: 'var(--primary)', 
                          textTransform: 'uppercase', 
                          letterSpacing: '0.05em',
                          marginBottom: '0.4rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.4rem'
                        }}>
                          <Tag size={10} /> {item.duration_budget || 'Süre Belirtilmedi'}
                        </div>
                        
                        <div style={{ 
                          fontSize: '0.8rem', 
                          color: 'rgba(255, 255, 255, 0.8)', 
                          lineHeight: '1.5',
                          fontStyle: item.notes ? 'normal' : 'italic',
                          display: '-webkit-box',
                          WebkitLineClamp: '2',
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {item.notes || 'Müşteri not bırakmadı.'}
                        </div>

                        {item.notes && item.notes.length > 60 && (
                          <button 
                            onClick={() => Swal.fire({
                              title: 'Teklif Notu',
                              text: item.notes,
                              background: '#0f172a',
                              color: '#fff',
                              confirmButtonColor: 'var(--primary)',
                              confirmButtonText: 'Kapat',
                              customClass: {
                                container: 'custom-swal-container'
                              }
                            })}
                            style={{ 
                              background: 'none', 
                              border: 'none', 
                              color: 'var(--primary)', 
                              fontSize: '0.7rem', 
                              fontWeight: '700', 
                              padding: 0, 
                              marginTop: '0.4rem', 
                              cursor: 'pointer',
                              textDecoration: 'underline'
                            }}
                          >
                            Tümünü Gör
                          </button>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '1.5rem' }}>
                      <span className={`badge ${item.status === 'Bekliyor' ? 'badge-warning' : 'badge-success'}`}>
                        {item.status}
                      </span>
                    </td>
                    <td style={{ padding: '1.5rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem' }}>
                        <Calendar size={14} color="var(--text-dim)" /> 
                        {new Date(item.created_at).toLocaleDateString()}
                      </div>
                    </td>
                    <td style={{ padding: '1.5rem', textAlign: 'right' }}>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                        {item.source === 'sell_request' && (
                          <button 
                            onClick={() => navigate('/admin/sell-requests', { state: { openId: item.id } })} 
                            style={{ background: 'rgba(0, 209, 255, 0.05)', border: 'none', color: 'var(--primary)', padding: '0.5rem', borderRadius: '0.75rem', cursor: 'pointer' }} 
                            title="Detaylı Gör"
                          >
                            <ExternalLink size={18} />
                          </button>
                        )}
                        <button onClick={() => handleDelete(item.id, item.source)} style={{ background: 'rgba(239, 68, 68, 0.05)', border: 'none', color: '#f87171', padding: '0.5rem', borderRadius: '0.75rem', cursor: 'pointer' }} title="Sil">
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.75rem', marginTop: '2rem', paddingBottom: '3rem', flexWrap: 'wrap' }}>
          <button 
            disabled={currentPage === 1}
            onClick={() => paginate(currentPage - 1)}
            style={{ 
              background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', 
              color: currentPage === 1 ? '#475569' : 'var(--primary)', 
              width: '42px', height: '42px', borderRadius: '0.75rem', 
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer', fontWeight: '700',
              display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s'
            }}
          >
            <ChevronLeft size={20} />
          </button>
          
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i + 1}
                onClick={() => paginate(i + 1)}
                style={{
                  width: '42px', height: '42px', borderRadius: '0.75rem', border: '1px solid',
                  borderColor: currentPage === i + 1 ? 'var(--primary)' : 'rgba(255,255,255,0.1)',
                  background: currentPage === i + 1 ? 'var(--primary)' : 'rgba(255,255,255,0.05)',
                  color: currentPage === i + 1 ? '#000' : 'white',
                  fontWeight: '800', cursor: 'pointer', transition: 'all 0.2s',
                  boxShadow: currentPage === i + 1 ? '0 0 15px rgba(0, 209, 255, 0.3)' : 'none',
                  transform: currentPage === i + 1 ? 'scale(1.05)' : 'scale(1)'
                }}
              >
                {i + 1}
              </button>
            ))}
          </div>

          <button 
            disabled={currentPage === totalPages}
            onClick={() => paginate(currentPage + 1)}
            style={{ 
              background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', 
              color: currentPage === totalPages ? '#475569' : 'var(--primary)', 
              width: '42px', height: '42px', borderRadius: '0.75rem', 
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', fontWeight: '700',
              display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s'
            }}
          >
            <ChevronRight size={20} />
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminQuotes;

